import { addYears, format, parseISO } from 'date-fns';
import type { AccrualFrequency, AccrualTier, Policy, PolicyForm, PolicyRule, PolicySection } from '../types/hr';
import { TODAY } from './format';

const UNIT: Record<AccrualFrequency, string> = {
  Monthly: 'month',
  'Per pay period': 'pay period',
  Annually: 'year',
};

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function days(n: string): string {
  return Number(n) === 1 ? 'day' : 'days';
}

function years(n: string | number): string {
  return Number(n) === 1 ? 'year' : 'years';
}

export function ordinal(n: string | number): string {
  const v = Number(n);
  const s = ['th', 'st', 'nd', 'rd'];
  const m = v % 100;
  return `${v}${s[(m - 20) % 10] || s[m] || s[0]}`;
}

export function hasSchedule(form: PolicyForm): form is PolicyForm & { schedule: AccrualTier[] } {
  return Array.isArray(form.schedule) && form.schedule.length > 0;
}

/** Tiers ordered by tenure; the starting schedule is always first. */
export function sortedTiers(schedule: AccrualTier[]): AccrualTier[] {
  return [...schedule].sort((a, b) => (Number(a.afterYears) || 0) - (Number(b.afterYears) || 0));
}

export function tierLabel(tier: AccrualTier, index: number): string {
  return index === 0 ? 'Starting schedule' : `Milestone ${index}`;
}

export function tierReached(tier: AccrualTier): string {
  const y = Number(tier.afterYears) || 0;
  return y === 0 ? 'From hire date' : `${y} ${years(y)} after hire`;
}

export function tierRate(tier: AccrualTier): string {
  if (!tier.days) return 'TBD';
  return `${tier.days} ${days(tier.days)} / ${tier.frequency === 'Yearly' ? 'year' : 'month'}`;
}

export function tierPosting(tier: AccrualTier): string {
  return tier.frequency === 'Yearly' ? `${tier.postMonth.slice(0, 3)} ${ordinal(tier.postDay)} each year` : `${ordinal(tier.postDay)} of each month`;
}

export function tierCap(tier: AccrualTier): string {
  return tier.capEnabled ? (tier.cap ? `Stops at ${tier.cap} days` : 'TBD') : 'No cap';
}

export function tierCarryover(tier: AccrualTier): string {
  return tier.carryoverEnabled ? (tier.carryover ? `Up to ${tier.carryover} days` : 'TBD') : 'Use it or lose it';
}

/** The tier that applies after `serviceYears` completed years. */
export function tierForYears(schedule: AccrualTier[], serviceYears: number): AccrualTier | undefined {
  return sortedTiers(schedule)
    .filter((t) => (Number(t.afterYears) || 0) <= serviceYears)
    .pop();
}

export function nextTier(schedule: AccrualTier[], serviceYears: number): AccrualTier | undefined {
  return sortedTiers(schedule).find((t) => (Number(t.afterYears) || 0) > serviceYears);
}

export function milestoneDate(hireIso: string, tier: AccrualTier): Date {
  return addYears(parseISO(hireIso), Number(tier.afterYears) || 0);
}

function sameValue(tiers: AccrualTier[], fn: (t: AccrualTier) => string): string | null {
  const values = Array.from(new Set(tiers.map(fn)));
  return values.length === 1 ? values[0] : null;
}

function describeSchedule(form: PolicyForm & { schedule: AccrualTier[] }): { sentence: string; tbd: string[] } {
  const tbd: string[] = [];
  const tiers = sortedTiers(form.schedule);
  const [start, ...milestones] = tiers;
  const wait = Number(form.waitingDays) || 0;

  if (tiers.some((t) => !t.days)) tbd.push('Accrual amount');
  if (tiers.some((t) => t.capEnabled && !t.cap)) tbd.push('Cap');
  if (tiers.some((t) => t.carryoverEnabled && !t.carryover)) tbd.push('Carryover');

  const startText = `Accrues ${start.days || 'TBD'} ${days(start.days)}/${start.frequency === 'Yearly' ? 'year' : 'month'} ${wait ? `after a ${wait}-day wait` : 'from hire'} (posted ${tierPosting(start).replace(' each year', '')})`;
  const steps = milestones.map((m, i) => `${m.days || 'TBD'} ${i === 0 ? 'days ' : ''}after ${m.afterYears || '?'} ${years(m.afterYears)}`);
  const stepText = steps.length ? `; steps up to ${steps.join(', ')}` : '';

  const cap = sameValue(tiers, tierCap);
  const carry = sameValue(tiers, tierCarryover);
  const capText = cap === null ? 'Cap varies by milestone' : cap === 'No cap' ? 'No balance cap' : `Balance cap: ${cap.toLowerCase()}`;
  const carryText =
    carry === null ? 'carryover varies by milestone' : carry === 'Use it or lose it' ? 'use it or lose it at year end' : `carries over ${carry.toLowerCase()}`;

  return { sentence: `${startText}${stepText}. ${capText}; ${carryText}.`, tbd };
}

/** Plain-English, one-paragraph description of a policy's rules. Blank values read as TBD. */
export function describePolicy(form: PolicyForm): { sentence: string; tbd: string[] } {
  const tbd: string[] = [];
  if (form.type === 'Unlimited') {
    return { sentence: 'Unlimited time off — requests are tracked for coverage, with no balance or accrual.', tbd };
  }
  if (hasSchedule(form)) return describeSchedule(form);

  const wait = Number(form.waitingDays) || 0;
  let accrual: string;
  if (form.accrualRate) {
    const verb = form.type === 'Fixed grant' ? 'Grants' : 'Accrues';
    accrual = `${verb} ${form.accrualRate} ${days(form.accrualRate)}/${UNIT[form.accrualFrequency]}${wait ? ` after ${wait} days` : ' from day one'}${form.prorate ? ', prorated' : ''}`;
  } else {
    tbd.push('Accrual rate');
    accrual = `Accrual rate TBD${wait ? ` (starts after ${wait} days)` : ''}`;
  }

  let cap: string;
  if (form.yearOneCap) {
    cap = `capped at ${form.yearOneCap} in year one`;
  } else {
    tbd.push('Cap');
    cap = 'cap TBD';
  }

  let anniversary: string;
  if (form.anniversaryAmount) {
    anniversary =
      form.anniversaryMode === 'top-up'
        ? `topped up to ${form.anniversaryAmount} at anniversary`
        : `+${form.anniversaryAmount} ${days(form.anniversaryAmount)} added at anniversary`;
  } else {
    tbd.push('Anniversary rule');
    anniversary = 'anniversary rule TBD';
  }

  let carryover: string;
  if (form.carryover) {
    carryover = `Carries over up to ${form.carryover} days.`;
  } else {
    tbd.push('Carryover');
    carryover = 'Carryover TBD.';
  }

  return { sentence: `${accrual}, ${cap}; ${anniversary}. ${carryover}`, tbd };
}

function rule(label: string, value: string): PolicyRule {
  return value && value !== 'TBD' ? { label, value } : { label, value: 'TBD', tbd: true };
}

function scheduleContent(form: PolicyForm & { schedule: AccrualTier[] }): { sections: PolicySection[]; highlights: PolicyRule[] } {
  const tiers = sortedTiers(form.schedule);
  const start = tiers[0];
  const top = tiers[tiers.length - 1];
  const wait = Number(form.waitingDays) || 0;
  const cap = sameValue(tiers, tierCap);
  const carry = sameValue(tiers, tierCarryover);
  const milestoneYears = tiers.slice(1).map((t) => t.afterYears);

  const sections: PolicySection[] = [
    {
      id: 'eligibility',
      title: 'Eligibility & waiting period',
      rules: [
        { label: 'Eligible departments', value: form.departments.length ? form.departments.join(', ') : 'None selected' },
        { label: 'Waiting period', value: wait ? `${wait} calendar days from hire date` : 'None — accrual begins immediately on hire' },
      ],
    },
    {
      id: 'accrual',
      title: 'Accrual',
      rules: [
        { label: 'Method', value: `Tenure-based schedule · ${tiers.length} ${tiers.length === 1 ? 'tier' : 'tiers'}` },
        rule('Starting accrual', tierRate(start)),
        { label: 'Posted', value: tierPosting(start) },
        { label: 'Tier applied', value: 'Based on completed years of service on the posting date' },
      ],
    },
    {
      id: 'caps',
      title: 'Caps',
      rules: [rule('Balance cap', cap ?? 'Varies by milestone — see schedule')],
    },
    {
      id: 'anniversary',
      title: 'Anniversary rule',
      rules: [
        {
          label: 'Milestones',
          value: milestoneYears.length ? `Accrual steps up at ${milestoneYears.join(', ')} ${years(milestoneYears[milestoneYears.length - 1])} of service` : 'No milestones — flat schedule',
        },
      ],
    },
    {
      id: 'carryover',
      title: 'Carryover',
      rules: [rule('Carryover limit', carry ?? 'Varies by milestone — see schedule'), { label: 'Carryover date', value: 'December 31' }],
    },
  ];

  const highlights: PolicyRule[] = [
    rule('Starting accrual', start.days ? `${start.days} days / ${start.frequency === 'Yearly' ? 'yr' : 'mo'}` : ''),
    rule('Top tier', top && tiers.length > 1 && top.days ? `${top.days} days at ${top.afterYears} yrs` : start.days ? `${start.days} days` : ''),
    rule('Carryover', carry ?? 'Varies'),
  ];

  return { sections, highlights };
}

/** Rebuild the read-only summary (highlights + sections) from the editor form. */
export function buildPolicy(base: Policy | undefined, form: PolicyForm, id: string): Policy {
  const unit = UNIT[form.accrualFrequency];
  const wait = Number(form.waitingDays) || 0;
  const amount = Number(form.anniversaryAmount) || 0;
  const unlimited = form.type === 'Unlimited';
  const { sentence, tbd } = describePolicy(form);
  const hasTbd = tbd.length > 0;
  const scheduled = hasSchedule(form) && !unlimited;

  const accrualValue = form.accrualRate ? `${form.accrualRate} ${days(form.accrualRate)} per ${unit}` : '';
  const anniversaryValue = amount
    ? form.anniversaryMode === 'top-up'
      ? `Top balance UP TO ${amount} days (not +${amount})`
      : `Add ${amount} days to the balance`
    : '';

  let sections: PolicySection[] = [
    {
      id: 'eligibility',
      title: 'Eligibility & waiting period',
      rules: [
        { label: 'Eligible departments', value: form.departments.length ? form.departments.join(', ') : 'None selected' },
        { label: 'Waiting period', value: wait ? `${wait} calendar days from hire date` : 'None' },
      ],
    },
    {
      id: 'accrual',
      title: 'Accrual',
      rules: unlimited
        ? [{ label: 'Accrual', value: 'Not applicable — unlimited policy' }]
        : [
            rule('Accrual rate', accrualValue),
            { label: 'Frequency', value: form.accrualFrequency },
            { label: 'Proration', value: form.prorate ? 'Yes — first partial period prorated' : 'No' },
          ],
    },
    { id: 'caps', title: 'Caps', rules: [rule('Year-one maximum', form.yearOneCap ? `${form.yearOneCap} days` : '')] },
    { id: 'anniversary', title: 'Anniversary rule', rules: [rule('Rule', anniversaryValue)] },
    { id: 'carryover', title: 'Carryover', rules: [rule('Carryover limit', form.carryover ? `${form.carryover} days` : '')] },
  ];

  let highlights: PolicyRule[] = [
    rule('Accrual', form.accrualRate ? `${form.accrualRate} ${days(form.accrualRate)} / ${unit}` : ''),
    rule('Year-one cap', form.yearOneCap ? `${form.yearOneCap} days` : ''),
    rule('Anniversary', amount ? (form.anniversaryMode === 'top-up' ? `Top up to ${amount}` : `Add ${amount}`) : ''),
  ];

  if (scheduled) {
    const content = scheduleContent(form as PolicyForm & { schedule: AccrualTier[] });
    sections = content.sections;
    highlights = content.highlights;
  }

  const keepOverview = base && base.hasTbd === hasTbd && Boolean(base.form.schedule?.length) === scheduled;

  return {
    id,
    name: form.name.trim(),
    tagline: base?.tagline ?? `Custom ${form.type.toLowerCase()} policy.`,
    type: form.type,
    updated: format(TODAY, 'yyyy-MM-dd'),
    hasTbd,
    overview: keepOverview && base ? base.overview : sentence,
    highlights,
    sections,
    example:
      !scheduled && form.anniversaryMode === 'top-up' && amount > 10
        ? {
            before: '10 days banked',
            added: `+${amount - 10} days`,
            after: `${amount} days`,
            caption: `The top-up brings the balance to ${amount} — it never adds a flat ${amount}. An employee with ${amount} or more receives nothing.`,
          }
        : undefined,
    form: { ...form, departments: [...form.departments], schedule: form.schedule?.map((t) => ({ ...t })) },
  };
}
